<template>
  <div
    class="pycad-vtk-frame"
    :class="{ 'pycad-frame--drawing': isDrawing }"
    tabindex="0"
    @pointerenter="hover = true"
    @pointerleave="hover = false"
    @focusin="hover = true"
    @focusout="hover = false"
  >
    <div class="pycad-vtk-stage" data-testid="two-view-container">
      <v-progress-linear
        v-if="isImageLoading"
        indeterminate
        class="pycad-loading"
        height="2"
        color="primary"
      />
      <div class="vtk-sub-container">
        <vtk-slice-view
          class="vtk-view"
          ref="vtkView"
          data-testid="vtk-view vtk-two-view"
          :view-id="viewId"
          :image-id="currentImageID"
          :view-direction="viewDirection"
          :view-up="viewUp"
        >
          <vtk-mouse-interaction-manipulator
            v-if="currentTool === Tools.Pan"
            :manipulator-constructor="vtkMouseCameraTrackballPanManipulator"
            :manipulator-props="{ button: 1 }"
          ></vtk-mouse-interaction-manipulator>
          <vtk-mouse-interaction-manipulator
            :manipulator-constructor="vtkMouseCameraTrackballPanManipulator"
            :manipulator-props="{ button: 1, shift: true }"
          ></vtk-mouse-interaction-manipulator>
          <vtk-mouse-interaction-manipulator
            :manipulator-constructor="vtkMouseCameraTrackballPanManipulator"
            :manipulator-props="{ button: 2 }"
          ></vtk-mouse-interaction-manipulator>
          <vtk-mouse-interaction-manipulator
            v-if="currentTool === Tools.Zoom"
            :manipulator-constructor="
              vtkMouseCameraTrackballZoomToMouseManipulator
            "
            :manipulator-props="{ button: 1 }"
          ></vtk-mouse-interaction-manipulator>
          <vtk-mouse-interaction-manipulator
            :manipulator-constructor="
              vtkMouseCameraTrackballZoomToMouseManipulator
            "
            :manipulator-props="{ button: 3 }"
          ></vtk-mouse-interaction-manipulator>
          <vtk-slice-view-slicing-manipulator
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          ></vtk-slice-view-slicing-manipulator>
          <vtk-slice-view-slicing-key-manipulator
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          ></vtk-slice-view-slicing-key-manipulator>
          <vtk-slice-view-window-manipulator
            :view-id="viewId"
            :image-id="currentImageID"
            :manipulator-config="windowingManipulatorProps"
          ></vtk-slice-view-window-manipulator>
          <PycadSliceViewerOverlay
            :view-id="viewId"
            :image-id="currentImageID"
            :show-slider-clearance="hasSliceSlider"
          >
            <template #top-right>
              <PycadViewControls
                :is-maximized="isMaximized"
                @reset="resetCamera"
                @toggle-maximize="toggleMaximize"
              >
                <DicomQuickInfoButton :image-id="currentImageID" />
              </PycadViewControls>
            </template>
            <template #bottom-right>
              <div
                v-if="!isLockedOrientationView"
                class="pycad-view-switcher-wrapper"
              >
                <ViewTypeSwitcher
                  :view-id="viewId"
                  :image-id="currentImageID"
                />
              </div>
            </template>
            <template v-if="isPaintActive" #bottom-left>
              <span class="pycad-paint-badge">
                <i class="mdi mdi-brush" />
                Paint
              </span>
            </template>
          </PycadSliceViewerOverlay>
          <vtk-base-slice-representation
            ref="baseSliceRep"
            :view-id="viewId"
            :image-id="currentImageID"
            :axis="viewAxis"
          ></vtk-base-slice-representation>
          <vtk-segmentation-slice-representation
            v-for="segId in segmentations"
            :key="`seg-${segId}`"
            :view-id="viewId"
            :segmentation-id="segId"
            :axis="viewAxis"
            ref="segSliceReps"
          ></vtk-segmentation-slice-representation>
          <template v-if="currentImageID">
            <vtk-layer-slice-representation
              v-for="layer in currentLayers"
              :key="`layer-${layer.id}`"
              ref="layerSliceReps"
              :view-id="viewId"
              :layer-id="layer.id"
              :parent-id="currentImageID"
              :axis="viewAxis"
            ></vtk-layer-slice-representation>
          </template>
          <component
            v-if="sliceTools.crop"
            :is="sliceTools.crop"
            :view-id="viewId"
            :image-id="currentImageID"
          />
          <crosshairs-tool
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <component
            v-if="sliceTools.paint"
            :is="sliceTools.paint"
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <component
            v-if="sliceTools.polygon"
            :is="sliceTools.polygon"
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <component
            v-if="sliceTools.ruler"
            :is="sliceTools.ruler"
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <component
            v-if="sliceTools.rectangle"
            :is="sliceTools.rectangle"
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <component
            v-if="sliceTools.angle"
            :is="sliceTools.angle"
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <component
            v-if="sliceTools.arrow"
            :is="sliceTools.arrow"
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <component
            v-if="sliceTools.circle"
            :is="sliceTools.circle"
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <component
            v-if="sliceTools.freehand"
            :is="sliceTools.freehand"
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <select-tool />
          <svg class="overlay-no-events">
            <bounding-rectangle :points="selectionPoints" />
          </svg>
          <scalar-probe
            :base-rep="baseSliceRep"
            :layer-reps="layerSliceReps"
            :segment-groups-reps="segSliceReps"
          ></scalar-probe>
          <slot></slot>
        </vtk-slice-view>
      </div>
    </div>

    <PycadSliceStrip
      v-if="hasSliceSlider"
      v-model="currentSlice"
      :min="sliceRange[0]"
      :max="sliceRange[1]"
      :axis="sliderAxisAbbrev"
      :color="sliderColor"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, toRefs, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import VtkSliceView from '@/src/components/vtk/VtkSliceView.vue';
import { VtkViewApi } from '@/src/types/vtk-types';
import { Tools } from '@/src/store/tools/types';
import VtkBaseSliceRepresentation from '@/src/components/vtk/VtkBaseSliceRepresentation.vue';
import VtkSegmentationSliceRepresentation from '@/src/components/vtk/VtkSegmentationSliceRepresentation.vue';
import { useSegmentGroupStore } from '@/src/store/segmentGroups';
import VtkLayerSliceRepresentation from '@/src/components/vtk/VtkLayerSliceRepresentation.vue';
import { useViewAnimationListener } from '@/src/composables/useViewAnimationListener';
import CrosshairsTool from '@/src/components/tools/crosshairs/CrosshairsTool.vue';
import SelectTool from '@/src/components/tools/SelectTool.vue';
import { enabledSliceTools } from '@/src/features/registry';
import ScalarProbe from '@/src/components/tools/ScalarProbe.vue';
import BoundingRectangle from '@/src/components/tools/BoundingRectangle.vue';
import DicomQuickInfoButton from '@/src/components/DicomQuickInfoButton.vue';
import ViewTypeSwitcher from '@/src/components/ViewTypeSwitcher.vue';
import PycadSliceViewerOverlay from '@/src/components/pycad/PycadSliceViewerOverlay.vue';
import PycadViewControls from '@/src/components/pycad/PycadViewControls.vue';
import PycadSliceStrip from '@/src/components/pycad/PycadSliceStrip.vue';
import { useToolSelectionStore } from '@/src/store/tools/toolSelection';
import { useAnnotationToolStore, useToolStore } from '@/src/store/tools';
import {
  doesToolFrameMatchViewAxis,
  useDrawingToolActive,
} from '@/src/composables/annotationTool';
import {
  applyCameraOp,
  clearViewRotation,
  onCameraOp,
} from '@/src/core/cameraOps';
import { isFeatureCompiled } from '@/src/features/featureFlags';
import { useWebGLWatchdog } from '@/src/composables/useWebGLWatchdog';
import { useSliceConfig } from '@/src/composables/useSliceConfig';
import VtkSliceViewWindowManipulator from '@/src/components/vtk/VtkSliceViewWindowManipulator.vue';
import VtkSliceViewSlicingManipulator from '@/src/components/vtk/VtkSliceViewSlicingManipulator.vue';
import VtkSliceViewSlicingKeyManipulator from '@/src/components/vtk/VtkSliceViewSlicingKeyManipulator.vue';
import VtkMouseInteractionManipulator from '@/src/components/vtk/VtkMouseInteractionManipulator.vue';
import vtkMouseCameraTrackballPanManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballPanManipulator';
import vtkMouseCameraTrackballZoomToMouseManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballZoomToMouseManipulator';
import { useResetViewsEvents } from '@/src/components/tools/ResetViews.vue';
import { onVTKEvent } from '@/src/composables/onVTKEvent';
import { useViewStore } from '@/src/store/views';
import { ViewInfo2D } from '@/src/types/views';
import { get2DViewingVectors } from '@/src/utils/getViewingVectors';

type Props = {
  viewId: string;
};

const vtkView = ref<VtkViewApi>();
const baseSliceRep = ref();
const layerSliceReps = ref([]);
const segSliceReps = ref([]);

const props = defineProps<Props>();
const { viewId } = toRefs(props);

const sliceTools = enabledSliceTools();

const viewStore = useViewStore();
const viewInfo = computed(() => viewStore.getView(viewId.value) as ViewInfo2D);

const {
  currentImageID,
  currentLayers,
  currentImageMetadata,
  currentImageData,
  isImageLoading,
} = useCurrentImage();

const viewAxis = computed(() => viewInfo.value.options.orientation);
const viewingVectors = computed(() => get2DViewingVectors(viewAxis.value));
const viewDirection = computed(() => viewingVectors.value.viewDirection);
const viewUp = computed(() => viewingVectors.value.viewUp);

const hover = ref(false);

function resetCamera() {
  clearViewRotation(viewId.value);
  vtkView.value?.resetCamera();
}

useResetViewsEvents().onClick(resetCamera);

// rotate / flip requests from the toolbar, targeted at this view
onCameraOp((event) => {
  if (event.viewId !== viewId.value || !vtkView.value) return;
  applyCameraOp(
    vtkView.value,
    viewId.value,
    event.op,
    currentImageMetadata.value?.worldBounds,
    event.angle
  );
});

useWebGLWatchdog(vtkView);
useViewAnimationListener(vtkView, viewId, '2D');

const { currentTool } = storeToRefs(useToolStore());
const isDrawing = useDrawingToolActive();

const { slice: currentSlice, range: sliceRange } = useSliceConfig(
  viewId,
  currentImageID
);

const windowingManipulatorProps = computed(() => {
  if (currentTool.value !== Tools.WindowLevel) {
    return { button: -1, dragEnabled: false };
  }
  return { button: 1 };
});

const isPaintActive = computed(() => currentTool.value === Tools.Paint);

onVTKEvent(currentImageData, 'onModified', () => {
  vtkView.value?.requestRender();
});

const segmentations = computed(() => {
  if (!currentImageID.value) return [];
  const store = useSegmentGroupStore();
  return store.orderByParent[currentImageID.value];
});

// --- View locked-orientation gating (mirrors original logic) --- //
const LOCKED_ORIENTATION_SUFFIXES = [
  '-coronal',
  '-sagittal',
  '-axial',
  '-multi-oblique',
];
const isLockedOrientationView = computed(() =>
  LOCKED_ORIENTATION_SUFFIXES.some((suffix) => viewId.value.includes(suffix))
);

// --- Slice-strip metadata (axis abbrev + color) --- //
const AXIS_META: Record<string, { abbrev: string; color: string }> = {
  Axial: { abbrev: 'AX', color: '#3B9EFF' },
  Sagittal: { abbrev: 'SA', color: '#FF5C5C' },
  Coronal: { abbrev: 'CO', color: '#4ADE80' },
};
const sliderAxisAbbrev = computed(
  () => AXIS_META[viewAxis.value]?.abbrev ?? 'SL'
);
const sliderColor = computed(
  () => AXIS_META[viewAxis.value]?.color ?? '#ff9900'
);
const hasSliceSlider = computed(
  () => !!currentImageID.value && sliceRange.value[1] > 0
);

// --- Maximize toggle --- //
const { activeView, isActiveViewMaximized } = storeToRefs(viewStore);
const isMaximized = computed(
  () => activeView.value === viewId.value && isActiveViewMaximized.value
);

function toggleMaximize() {
  viewStore.setActiveView(viewId.value);
  viewStore.toggleActiveViewMaximized();
}

// --- Selection (annotation handles) --- //
const selectionStore = useToolSelectionStore();
const selectionPoints = computed(() => {
  if (!isFeatureCompiled('annotations')) return [];

  return selectionStore.selection
    .map((sel) => {
      const store = useAnnotationToolStore(sel.type);
      return { store, tool: store.toolByID[sel.id] };
    })
    .filter(
      ({ tool }) =>
        tool.slice === currentSlice.value &&
        !tool.hidden &&
        doesToolFrameMatchViewAxis(viewAxis, tool, currentImageMetadata)
    )
    .flatMap(({ store, tool }) => store.getPoints(tool.id));
});
</script>

<style scoped src="@/src/components/styles/vtk-view.css"></style>
<style scoped src="@/src/components/styles/utils.css"></style>
<style scoped src="@/src/components/pycad/pycad-vtk-view.css"></style>

<style scoped>
.pycad-view-switcher-wrapper {
  padding: 4px;
  background: rgba(5, 5, 5, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
}
</style>
